'use client';

import {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  ButtonGroup,
  type ButtonGroupProps,
  type ButtonProps,
  SkeletonTextLines,
  TextButton,
  type TextButtonProps,
} from '../../../core-components';
import {
  FriendlyCaptcha
} from '../FriendlyCaptcha';
import { HeaderSection, type HeaderSectionProps } from '../HeaderSection';

import type { DataAttributes } from '../../../utils';
import type {
  FriendlyCaptchaProps,
  FriendlyCaptchaRef} from '../FriendlyCaptcha';
import type {
  ReactNode,
  FormHTMLAttributes,
  FormEventHandler,
  FormEvent} from 'react';

type ElementAttributes = Omit<
  FormHTMLAttributes<HTMLFormElement>,
  'children' | 'onSubmit'
> &
  DataAttributes;

type FormState = {
  pending: boolean;
  disabled: boolean;
};

export type FormSubmitCallbacks = {
  resetForm: () => void;
  getCaptchaSolution: () => string | undefined;
  keepPending: () => void;
  keepDisabled: () => void;
};

export type FormProps = ElementAttributes & {
  fallback?: boolean;
  children?: ReactNode | ((state: FormState) => ReactNode);
  header?: HeaderSectionProps;
  meta?: ReactNode;
  footNote?: ReactNode;
  submitButton?: ButtonProps;
  cancelButton?: TextButtonProps;
  buttonGroup?: ButtonGroupProps;
  captcha?: FriendlyCaptchaProps;
  onSubmit?: (
    ev: FormEvent<HTMLFormElement>,
    callbacks: FormSubmitCallbacks,
  ) => Promise<void> | void;
};

export type FormRef = {
  resetForm: () => void;
};

export const Form = forwardRef(
  (
    {
      fallback,
      children,
      header,
      meta,
      footNote,
      submitButton,
      cancelButton,
      onSubmit,
      buttonGroup,
      captcha,
      ...attrs
    }: FormProps,
    ref,
  ) => {
    const [pending, setPending] = useState(false);
    const [keepPending, setKeepPending] = useState(false);
    const [keepDisabled, setKeepDisabled] = useState(false);
    const [captchaSolved, setCaptchaSolved] = useState(
      typeof captcha === 'undefined',
    );

    const captchaRef = useRef<FriendlyCaptchaRef>(null);

    const formState: FormState = {
      pending: keepPending || pending,
      disabled: keepDisabled || pending,
    };

    const resetForm = () => {
      captchaRef.current?.reset();
      setCaptchaSolved(typeof captcha === 'undefined');
      setPending(false);
    };

    useImperativeHandle(
      ref,
      () =>
        ({
          resetForm,
        }) satisfies FormRef,
    );

    const handleSubmit: FormEventHandler<HTMLFormElement> = async ev => {
      if (!onSubmit) {
        return;
      }

      try {
        setPending(true);

        await onSubmit(ev, {
          getCaptchaSolution: () => captchaRef.current?.getSolution(),
          resetForm,
          keepPending: () => {
            setKeepPending(true);
          },
          keepDisabled: () => {
            setKeepDisabled(true);
          },
        });
      } finally {
        setPending(false);
      }
    };

    const renderedChildren =
      typeof children === 'function' ? children(formState) : children;

    return (
      <form
        {...attrs}
        className={twMerge(attrs.className, 'space-y-8')}
        onSubmit={handleSubmit}
      >
        {header && <HeaderSection {...header} fallback={fallback} />}

        {renderedChildren && (
          <div className="space-y-8">{renderedChildren}</div>
        )}

        {captcha && (
          <FriendlyCaptcha
            {...captcha}
            ref={captchaRef}
            onSuccess={() => setCaptchaSolved(true)}
          />
        )}

        {fallback && <SkeletonTextLines number={2} className="h-1.5" />}
        {!fallback && meta && (
          <div className="info-md text-marine-high-emphasis">{meta}</div>
        )}

        {(fallback || submitButton || cancelButton || footNote) && (
          <div className="space-y-3">
            {fallback && (
              <ButtonGroup>
                <Button fallback />
                <Button fallback />
              </ButtonGroup>
            )}

            {!fallback && (submitButton || cancelButton) && (
              <ButtonGroup {...buttonGroup}>
                {submitButton && (
                  <Button
                    {...submitButton}
                    className="w-full sm:w-auto"
                    type="submit"
                    variant={submitButton.variant ?? 'primary'}
                    disabled={
                      submitButton.disabled ??
                      (keepDisabled || pending || !captchaSolved)
                    }
                    indicating={
                      submitButton.indicating ?? (keepPending || pending)
                    }
                  />
                )}

                {cancelButton && (
                  <div className="text-center">
                    <TextButton
                      {...cancelButton}
                      type="reset"
                      disabled={
                        cancelButton.disabled ?? (keepDisabled || pending)
                      }
                    />
                  </div>
                )}
              </ButtonGroup>
            )}

            {fallback && <SkeletonTextLines number={1} className="h-1.5" />}
            {!fallback && footNote && (
              <div className="info-md text-marine-high-emphasis">
                {footNote}
              </div>
            )}
          </div>
        )}
      </form>
    );
  },
);
Form.displayName = 'Form';
